<section class="audit-page" aria-labelledby="page-title">
  <header class="page-header flex align-start justify-between gap-16 mb-20">
    <div>
      <h1 id="page-title" class="page-title fs-22 fw-700 m-0">Audit Log</h1>
      <p class="page-subtitle m-0 mt-5">
        System-wide activity log — all patients and users, sorted by most recent event. Filter by
        patient or user to narrow results.
      </p>
    </div>

    <!-- Two different inert states, deliberately.
         While an export runs the button is `aria-disabled`, not `disabled`: the browser blurs a
         control the moment it becomes disabled, which would drop keyboard focus out of the page
         header the instant the export starts and leave `aria-busy` on an unfocused element where it
         goes unannounced. `exportCsv()` enforces `canExport()` itself, so the control is inert
         anyway. When it is inert for any other reason (no resolved search, a load error, zero
         matches) it is really `disabled` — there is no focus to preserve, and an aria-disabled
         button swallows a keyboard Enter in total silence. -->
    <button
      #exportButton
      type="button"
      class="export-button secondary-button fw-600"
      [disabled]="!isExporting() && !canExport()"
      [class.export-button--exporting]="isExporting()"
      [attr.aria-disabled]="isExporting() ? 'true' : null"
      [attr.aria-busy]="isExporting()"
      (click)="exportCsv()"
    >
      @if (isExporting()) {
      <!-- `<mat-spinner>` carries role="progressbar", which has no business inside a role=button
           whose descendants should be presentational — the label already says what's happening. -->
      <span class="flex align-center" aria-hidden="true">
        <app-progress-spinner [diameter]="16" />
      </span>
      } @else {
      <app-icon
        name="download"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      }
      <span>{{ isExporting() ? 'Preparing export…' : exportLabel() }}</span>
    </button>

    <!-- A download is invisible to a screen reader — the file just lands in the browser's download
         shelf — and `aria-busy` on the button above is not an announced state change, so the start,
         the completion and a cancellation are all narrated here instead. -->
    <span class="sr-only" role="status">{{ exportStatus() }}</span>
  </header>

  <!-- Sits between the heading and the card, directly under the Export CSV button that produced it:
       a failed export is answered with a message rather than a file, so the message belongs beside
       the control, not buried inside the results card. -->
  @if (exportError(); as exportError) {
  <div class="export-error flex align-start justify-between gap-12" role="alert">
    <p class="m-0">{{ exportError }}</p>
    <button
      type="button"
      class="export-error__dismiss"
      aria-label="Dismiss export error"
      (click)="clearExportError()"
    >
      <app-icon
        name="close"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </button>
  </div>
  }

  <section class="log-card" aria-labelledby="log-heading">
    <h2 id="log-heading" class="sr-only">System-wide audit log</h2>

    <div class="filter-bar flex gap-12 align-center flex-wrap">
      <!-- Patient + User search share a single bordered group split by a hairline
           divider (see wireframe), rather than reading as two separate controls. -->
      <div class="search-group flex">
        <!-- Pill and its clear affordance are sibling buttons in a wrapper, never
             nested (interactive-in-interactive is invalid HTML and mishandled by AT). -->
        <div class="filter-pill-group">
          <button
            type="button"
            class="filter-pill"
            [class.filter-pill--active]="patientFilterSummary()"
            (click)="openPatientModal()"
          >
            <span class="filter-pill__icon" aria-hidden="true">
              <app-icon
                name="person_search"
                size="xs"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
            </span>
            <span class="filter-pill__text">
              <span class="filter-pill__label">Patient</span>
              <span class="filter-pill__value">{{ patientFilterSummary() ?? 'Name or MRN…' }}</span>
            </span>
          </button>
          @if (patientFilterSummary()) {
          <button
            type="button"
            class="filter-pill__clear"
            aria-label="Clear patient filter"
            (click)="clearPatientFilter()"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>

        <span class="search-group__divider" aria-hidden="true"></span>

        <div class="filter-pill-group">
          <button
            type="button"
            class="filter-pill"
            [class.filter-pill--active]="userFilterSummary()"
            (click)="openUserModal()"
          >
            <span class="filter-pill__icon filter-pill__icon--user" aria-hidden="true">
              <app-icon
                name="assignment_ind"
                size="xs"
                color="custom"
                customColor="currentColor"
                role="presentation"
              ></app-icon>
            </span>
            <span class="filter-pill__text">
              <span class="filter-pill__label">User / Employee</span>
              <span class="filter-pill__value">{{ userFilterSummary() ?? 'Name…' }}</span>
            </span>
          </button>
          @if (userFilterSummary()) {
          <button
            type="button"
            class="filter-pill__clear"
            aria-label="Clear user filter"
            (click)="clearUserFilter()"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
      </div>

      <!-- Vertical rule separating the search group from the dropdown filters. -->
      <span class="filter-divider" aria-hidden="true"></span>

      <app-dropdown
        label="Date Range"
        ariaLabel="Filter events by date range"
        [options]="dateRangeOptions"
        [value]="selectedDateRange()"
        (valueChange)="onDateRangeChange($event)"
      />

      <app-dropdown
        label="Action Type"
        ariaLabel="Filter events by action type"
        [options]="actionTypeFilterOptions"
        [value]="selectedActionType()"
        (valueChange)="onActionTypeChange($event)"
      />

      <label class="flagged-toggle flex align-center gap-8">
        <span class="flagged-toggle__label flex align-center gap-4">
          <app-icon
            name="flag"
            size="xs"
            color="custom"
            customColor="currentColor"
            role="presentation"
          ></app-icon>
          Flagged only
        </span>
        <span class="switch">
          <input
            type="checkbox"
            [ngModel]="flaggedOnly()"
            (ngModelChange)="onFlaggedOnlyChange($event)"
            aria-label="Show flagged events only"
          />
          <span class="switch__track" aria-hidden="true"></span>
        </span>
      </label>
    </div>

    <div class="table-shell">
      @if (isLoading()) {
      <div
        class="table-status flex flex-col align-center justify-center text-center"
        role="status"
        aria-live="polite"
      >
        <app-progress-spinner [diameter]="48" />
        <p>Loading audit log…</p>
      </div>
      } @else if (loadError(); as loadError) {
      <div
        class="table-status table-status--error flex flex-col align-center justify-center text-center"
        aria-live="polite"
      >
        <h3>Unable to load the audit log</h3>
        <p>{{ loadError }}</p>
      </div>
      } @else if (rows().length === 0) {
      <div
        class="table-status table-status--empty flex flex-col align-center justify-center text-center"
        aria-live="polite"
      >
        <h3>{{ hasActiveFilters() ? 'No matching events' : 'No activity recorded' }}</h3>
        <p>
          {{ hasActiveFilters() ? 'Try adjusting the date range, action type, or filters to broaden
          the results.' : 'There are no recorded audit events in the selected range.' }}
        </p>
      </div>
      } @else {
      <div class="log-table" role="table" aria-label="System-wide audit log">
        <div class="log-table__header log-table__grid gap-12 fw-600 uppercase" role="row">
          <div role="columnheader">Date &amp; Time</div>
          <div role="columnheader">Patient</div>
          <div role="columnheader">MRN</div>
          <div role="columnheader">Event / Action</div>
          <div role="columnheader">Performed By</div>
          <div role="columnheader">Type</div>
          <div role="columnheader" class="text-center">Flag</div>
        </div>

        <div class="log-table__body" role="rowgroup">
          @for (row of rows(); track row.trackKey) {
          <!--
            Appointment and medication reads have no detail screen, so those rows are inert: the tab
            stop is removed (a focusable element that does nothing is a dead end for a keyboard user)
            and the modifier class drops the pointer/hover affordances. `onRowClick` refuses them too
            — the handlers stay bound because a conditional listener would mean duplicating the row.
          -->
          <div
            class="log-table__row log-table__grid gap-12"
            [class.log-table__row--static]="!row.detailRoute"
            role="row"
            [attr.tabindex]="row.detailRoute ? 0 : null"
            [attr.aria-label]="row.ariaLabel"
            (click)="onRowClick(row)"
            (keydown.enter)="onRowClick(row)"
            (keydown.space)="onRowSpace($event, row)"
          >
            <div class="cell cell--datetime" role="cell">
              <p class="cell__primary">{{ row.date }}</p>
              <p class="cell__secondary">{{ row.time }}</p>
            </div>

            <div class="cell cell--patient" role="cell">
              <!--
                Admin actions have no subject patient, so the cell shows the em-dash placeholder.
                Skip the link styling there so a placeholder never reads as a patient name, and on a
                row that doesn't open a detail screen, so nothing in it reads as a link.
              -->
              <p class="fw-600" [class.cell__link]="row.isDisclosure && !!row.detailRoute">
                {{ row.patientName }}
              </p>
            </div>

            <div class="cell cell--mrn" role="cell">
              <p class="cell__primary">{{ row.medicalRecordNumber }}</p>
            </div>

            <div class="cell cell--event" role="cell">
              <p class="cell__event" [class.cell__event--flagged]="row.isFlagged">
                {{ row.eventLabel }}
              </p>
              @if (row.eventDetail) {
              <p class="cell__secondary">{{ row.eventDetail }}</p>
              }
            </div>

            <div class="cell cell--actor" role="cell">
              <p class="cell__primary fw-600">{{ row.performedByName }}</p>
              @if (row.performedByRole) {
              <p class="cell__secondary">{{ row.performedByRole }}</p>
              }
            </div>

            <div class="cell cell--type" role="cell">
              <span class="type-badge" [class]="'type-badge--' + row.typeClass"
                >{{ row.typeCategory }}</span
              >
            </div>

            <div class="cell cell--flag text-center" role="cell">
              @if (row.isFlagged) {
              <app-icon
                class="flag-icon"
                name="flag"
                size="sm"
                color="custom"
                customColor="currentColor"
                [ariaLabel]="'Flagged event'"
              ></app-icon>
              } @else {
              <span class="flag-empty" aria-hidden="true">—</span>
              }
            </div>
          </div>
          }
        </div>
      </div>

      <footer class="pagination flex align-center justify-between gap-12" aria-label="Pagination">
        <p class="pagination__summary m-0 fs-12">{{ showingSummary() }}</p>

        <nav
          class="pagination__controls flex flex-wrap align-center justify-end gap-4"
          aria-label="Page controls"
        >
          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() - 1)"
            [disabled]="currentPage() === 1"
            aria-label="Previous page"
          >
            <app-icon
              name="chevron_left"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>

          @for (token of pageTokens(); track $index) { @if (token === 'ellipsis') {
          <span class="page-ellipsis" aria-hidden="true">…</span>
          } @else {
          <button
            type="button"
            class="page-button fs-12 fw-600 cursor"
            [class.page-button--active]="token === currentPage()"
            [attr.aria-current]="token === currentPage() ? 'page' : null"
            (click)="goToPage(token)"
          >
            {{ token }}
          </button>
          } }

          <button
            type="button"
            class="page-button cursor"
            (click)="goToPage(currentPage() + 1)"
            [disabled]="currentPage() === totalPages()"
            aria-label="Next page"
          >
            <app-icon
              name="chevron_right"
              size="sm"
              color="custom"
              customColor="currentColor"
              role="presentation"
            />
          </button>
        </nav>

        <div class="rows-per-page flex align-center gap-8">
          <label class="fs-12" for="rows-per-page">Rows per page:</label>
          <select
            id="rows-per-page"
            class="filter-select filter-select--compact"
            [ngModel]="pageSize()"
            (ngModelChange)="onPageSizeChange($event)"
            aria-label="Rows per page"
          >
            @for (option of pageSizeOptions; track option) {
            <option [value]="option">{{ option }}</option>
            }
          </select>
        </div>
      </footer>
      }
    </div>
  </section>

  @if (activeModal() === 'patient') {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <app-patient-search-modal
      subtitle="Enter details to find a patient and filter the audit log to their activity only."
      [initialFilter]="patientFilter()"
      (applied)="applyPatientFilter($event)"
      (cleared)="clearPatientFilter()"
      (closed)="closeModal()"
      (click)="$event.stopPropagation()"
    ></app-patient-search-modal>
  </div>
  } @if (activeModal() === 'user') {
  <div class="modal-backdrop" role="presentation" (click)="closeModal()">
    <app-user-search-modal
      [initialFilter]="adminUserFilter()"
      (applied)="applyUserFilter($event)"
      (cleared)="clearUserFilter()"
      (closed)="closeModal()"
      (click)="$event.stopPropagation()"
    ></app-user-search-modal>
  </div>
  }
</section>
